import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchDashboardStats, fetchAdminOrders, fetchAdminProducts, getProductStock } from '../lib/adminApi';
import { formatPrice } from '../../utils/formatPrice';

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const [statsData, ordersData, productsData] = await Promise.all([
          fetchDashboardStats().catch(() => null),
          fetchAdminOrders(),
          fetchAdminProducts(),
        ]);

        if (cancelled) return;

        setStats(statsData);
        setOrders((ordersData || []).slice(0, 6));

        const low = (productsData || [])
          .map(p => ({ ...p, stock: getProductStock(p) }))
          .filter(p => p.stock.available > 0 && p.stock.available <= 10)
          .slice(0, 6);
        setLowStock(low);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Could not load dashboard.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    const interval = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 className="admin-page-title">Dashboard</h1>
        <p className="admin-page-subtitle">Overview of your store performance</p>
      </div>

      {error && <p className="admin-error">{error}</p>}
      {loading && <p className="admin-empty">Loading dashboard...</p>}

      {!loading && (
        <>
          <div className="admin-stats">
            <div className="admin-stat">
              <p className="admin-stat-label">Revenue</p>
              <p className="admin-stat-value">
                {formatPrice(stats?.revenue || orders.reduce((s, o) => s + Number(o.total || 0), 0))}
              </p>
            </div>
            <div className="admin-stat">
              <p className="admin-stat-label">Orders</p>
              <p className="admin-stat-value">{stats?.orders_count ?? orders.length}</p>
            </div>
            <div className="admin-stat">
              <p className="admin-stat-label">Customers</p>
              <p className="admin-stat-value">{stats?.customers_count ?? '—'}</p>
            </div>
            <div className="admin-stat">
              <p className="admin-stat-label">Products</p>
              <p className="admin-stat-value">{stats?.products_count ?? '—'}</p>
            </div>
          </div>

          <div className="admin-grid-2">
            <div className="admin-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 className="admin-card-title">Recent orders</h2>
                <Link to="/admin/orders" className="admin-btn admin-btn-ghost admin-btn-sm">View all</Link>
              </div>
              {orders.length === 0 ? (
                <p className="admin-empty">No orders yet.</p>
              ) : (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Order</th>
                        <th>Customer</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map(order => (
                        <tr key={order.id}>
                          <td>
                            <Link to={`/admin/orders/${order.id}`}>
                              {order.order_number || order.id.slice(0, 8)}
                            </Link>
                          </td>
                          <td>{order.shipping_name}</td>
                          <td>{formatPrice(order.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="admin-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 className="admin-card-title">Low stock</h2>
                <Link to="/admin/products" className="admin-btn admin-btn-ghost admin-btn-sm">Manage</Link>
              </div>
              {lowStock.length === 0 ? (
                <p className="admin-empty">No low-stock products.</p>
              ) : (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Available</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lowStock.map(product => (
                        <tr key={product.id}>
                          <td>
                            <Link to={`/admin/products/${product.id}`}>{product.name}</Link>
                          </td>
                          <td>{product.stock.available}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Dashboard;
